import { type ReactNode, useEffect, useId, useRef, useState } from "react";

import { cn } from "@/lib/utils";

interface AutoApplyToggleProps {
  value: boolean;
  onChange: (next: boolean) => void;
  marketplaceLabel: string;
  disabled?: boolean;
  ariaLabel?: string;
  confirmBody?: ReactNode;
  /** `kind` del canale: decide su COSA diciamo che verrà scritto. */
  kind?: string;
  /** Margine minimo vigente, mostrato come guardrail nella conferma. */
  minMarginPct?: string;
}

/**
 * Destinazione REALE della scrittura, per canale.
 *
 * La conferma diceva "su Poleepo e Mirakl" per tutti: falso per Amazon
 * diretto, per BricoBravo e per il sito WooCommerce. Nel momento di massimo
 * rischio l'interfaccia nominava i sistemi sbagliati.
 */
const WRITE_TARGET: Record<string, string> = {
  mirakl: "Mirakl (OF24) e, in secondo piano, Poleepo",
  amazon: "Amazon (SP-API Listings)",
  poleepo: "Poleepo",
  woocommerce: "il sito WooCommerce",
};

export function AutoApplyToggle({
  value,
  onChange,
  marketplaceLabel,
  disabled = false,
  ariaLabel,
  confirmBody,
  kind,
  minMarginPct,
}: AutoApplyToggleProps): JSX.Element {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  // <dialog> nativo: Escape, focus trap e ::backdrop li fa il browser. Non
  // li reimplementiamo a mano (la versione precedente non ne aveva nessuno).
  useEffect(() => {
    const node = dialogRef.current;
    if (!node) return;
    if (!node.open) {
      // showModal manca in ambienti senza supporto <dialog> (jsdom vecchi):
      // l'attributo `open` degrada a modale non bloccante invece che a nulla.
      if (typeof node.showModal === "function") node.showModal();
      else node.setAttribute("open", "");
    }
  }, [confirmOpen]);

  const handleClick = (): void => {
    if (disabled) return;
    // Spegnere non chiede conferma: l'interruttore di emergenza non deve
    // avere attrito. Accendere sì.
    if (value) {
      onChange(false);
      return;
    }
    setConfirmOpen(true);
  };

  const target = (kind && WRITE_TARGET[kind]) ?? "i canali collegati";

  return (
    <>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={ariaLabel ?? `Auto-apply ${marketplaceLabel}`}
        disabled={disabled}
        onClick={handleClick}
        className={cn(
          "relative h-6 w-11 rounded-full border transition focus:outline-none focus:ring-2 focus:ring-ink/40",
          value ? "border-accent bg-accent" : "border-line bg-paper",
          disabled && "opacity-50",
        )}
      >
        <span
          className={cn(
            "absolute top-0.5 h-5 w-5 rounded-full bg-card shadow transition",
            value ? "left-5" : "left-0.5",
          )}
        />
      </button>

      {confirmOpen && (
        <dialog
          ref={dialogRef}
          aria-labelledby={titleId}
          onClose={() => setConfirmOpen(false)}
          onClick={(event) => {
            // click sullo scrim (il target è il dialog stesso, non il contenuto)
            if (event.target === dialogRef.current) setConfirmOpen(false);
          }}
          className="scrim-dialog w-full max-w-md rounded-[var(--radius-lg)] border border-line bg-card p-6 text-foreground shadow-panel backdrop:bg-rail/40"
        >
          <h2 id={titleId} className="text-xl font-semibold tracking-tight">
            Attivare auto-apply?
          </h2>
          {confirmBody ?? (
            <div className="mt-2 space-y-2 text-sm text-ink-3">
              <p>
                Da questo momento i prezzi di <strong>{marketplaceLabel}</strong> verranno scritti
                su <strong>{target}</strong> senza ulteriore conferma.
              </p>
              <ul className="tape space-y-1 font-sans text-md">
                <li>
                  · Nessun prezzo scende sotto il margine minimo
                  {minMarginPct ? ` (${minMarginPct}%)` : ""}.
                </li>
                <li>· Vale solo per i fornitori attivi nel pannello qui sopra.</li>
                <li>
                  · Per fermarlo: rimetti il toggle su OFF e premi Salva. Ha effetto dal giro
                  successivo.
                </li>
              </ul>
            </div>
          )}
          <div className="mt-6 flex justify-end gap-2">
            <button type="button" className="btn btn-ghost" onClick={() => setConfirmOpen(false)}>
              Annulla
            </button>
            <button
              type="button"
              className="btn btn-accent"
              autoFocus
              onClick={() => {
                setConfirmOpen(false);
                onChange(true);
              }}
            >
              Conferma
            </button>
          </div>
        </dialog>
      )}
    </>
  );
}
